import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="smart-header"
// Hides the header when scrolling down on mobile, shows it when scrolling up
export default class extends Controller {
  static targets = ["header"]
  static values = {
    threshold: { type: Number, default: 10 }
  }

  declare readonly headerTarget: HTMLElement
  declare readonly hasHeaderTarget: boolean
  declare readonly thresholdValue: number

  private lastScrollTop: number = 0
  private ticking: boolean = false
  private isHidden: boolean = false
  private menuOpen: boolean = false
  private boundHandleScroll: () => void
  private boundHandleResize: () => void
  private boundHandleMenuOpened: (event: Event) => void
  private boundHandleMenuClosed: (event: Event) => void

  connect() {
    this.boundHandleScroll = this.handleScroll.bind(this)
    this.boundHandleResize = this.handleResize.bind(this)
    this.boundHandleMenuOpened = this.handleMenuOpened.bind(this)
    this.boundHandleMenuClosed = this.handleMenuClosed.bind(this)

    // Listen to window scroll (document-level scrolling)
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundHandleResize, { passive: true })

    // Listen for side menu events to coordinate header visibility
    this.element.addEventListener('side-menu:menuOpened', this.boundHandleMenuOpened)
    this.element.addEventListener('side-menu:menuClosed', this.boundHandleMenuClosed)

    this.syncDesktopHeaderState()
  }

  disconnect() {
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundHandleResize)
    this.element.removeEventListener('side-menu:menuOpened', this.boundHandleMenuOpened)
    this.element.removeEventListener('side-menu:menuClosed', this.boundHandleMenuClosed)
  }

  private handleScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateHeader()
        this.ticking = false
      })
      this.ticking = true
    }
  }

  private handleResize() {
    this.syncDesktopHeaderState()
  }

  private handleMenuOpened() {
    this.menuOpen = true
    // Force header visible when side menu opens
    if (this.isHidden) {
      this.isHidden = false
      this.headerTarget.classList.remove('-translate-y-full')
      this.dispatch('shown')
    }
    // Switch from sticky to fixed so header stays visible when overflow:hidden
    // breaks sticky positioning. Add padding to compensate for leaving document flow.
    const headerHeight = this.headerTarget.offsetHeight
    this.headerTarget.classList.remove('sticky')
    this.headerTarget.classList.add('fixed')
    document.body.style.paddingTop = `${headerHeight}px`
  }

  private handleMenuClosed() {
    this.menuOpen = false
    // Restore sticky positioning and remove compensating padding
    this.headerTarget.classList.remove('fixed')
    this.headerTarget.classList.add('sticky')
    document.body.style.paddingTop = ''
    // Reset scroll tracking to current position so header doesn't immediately hide
    this.lastScrollTop = window.scrollY || document.documentElement.scrollTop
  }

  private updateHeader() {
    // Only apply on mobile; skip while side menu is open (scroll is locked anyway)
    if (window.innerWidth >= 768 || !this.hasHeaderTarget || this.menuOpen) {
      return
    }

    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const headerHeight = this.headerTarget.offsetHeight
    const delta = scrollTop - this.lastScrollTop

    // Option 1: Always show header when near the top (within header height)
    // This prevents the gray gap when scrolled to or near the top
    if (scrollTop <= headerHeight) {
      this.showHeader()
      this.lastScrollTop = scrollTop
      return
    }

    // Option 2: Use more sensitive threshold when close to top
    // Makes it easier to reveal header when approaching the top
    const showThreshold = scrollTop < 150 ? 3 : this.thresholdValue

    // Scrolling down - hide header (only after scrolling past header height)
    if (delta > this.thresholdValue && scrollTop > headerHeight) {
      this.hideHeader()
    }
    // Scrolling up - show header (with adaptive threshold)
    else if (delta < -showThreshold) {
      this.showHeader()
    }

    this.lastScrollTop = scrollTop
  }

  private hideHeader() {
    if (this.isHidden) return

    this.isHidden = true
    this.headerTarget.classList.add('-translate-y-full')

    // Dispatch custom event for other components that might need to react
    this.dispatch('hidden')
  }

  private showHeader() {
    if (!this.isHidden) return

    this.isHidden = false
    this.headerTarget.classList.remove('-translate-y-full')

    // Dispatch custom event for other components that might need to react
    this.dispatch('shown')
  }

  private syncDesktopHeaderState() {
    if (!this.hasHeaderTarget || window.innerWidth < 768) return

    this.isHidden = false
    this.headerTarget.classList.remove('-translate-y-full')

    if (this.menuOpen) return

    this.headerTarget.classList.remove('fixed')
    this.headerTarget.classList.add('sticky')
    document.body.style.paddingTop = ''
  }
}
